import { getCurrency } from 'utils/utils';
import { Price } from '../interfaces/HotelDetails';
import { AvailableBoardType, Hotel, HotelRoom } from '../interfaces/HotelResponse';
import { getKey } from 'utils/hotel';
import { faAccessibleIcon } from '@fortawesome/free-brands-svg-icons';
import {
  faBed,
  faBuilding,
  faEye,
  faHouse,
  faTag,
  faUser,
  faUsers,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import { faBedFront } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { moManualRoomTypeOptions } from 'constants/hotel';

export enum RoomType {
  brd = 'brd',
  bdr = 'bdr',
  typ = 'typ',
  vew = 'vew',
  bed = 'bed',
  ada = 'ada',
  pax = 'pax',
  cat = 'cat',
  blc = 'blc',
  cap = 'cap',
  sgl = 'sgl',
  shr = 'shr',
}
export interface BoardType {
  code: string;
  name: string;
}

export interface SupplierItem {
  suppDesc: string;
  suppCode: string;
  checked: boolean;
  hotels: number;
  rooms: number;
  price: number;
  hotelsArr?: string[];
}

export const roomTypesIcon = {
  [RoomType.ada]: faAccessibleIcon,
  [RoomType.brd]: faUtensils,
  [RoomType.bdr]: faBedFront,
  [RoomType.typ]: faHouse,
  [RoomType.vew]: faEye,
  [RoomType.bed]: faBed,
  [RoomType.pax]: faUser,
  [RoomType.cat]: faTag,
  [RoomType.blc]: faBuilding,
  [RoomType.cap]: faUsers,
};

export interface RoomTypeGrp {
  title: string;
  options: RoomTypeOption[];
  icon?: any;
}

export interface BedRule {
  active?: number | string | null;
  negative: string[];
  positive: string[];
  title_lang?: string;
}

export interface BedRuleGroup {
  type: string;
  items: BedRule[];
}

export const getSearchRoomsString = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return (
    new URLSearchParams(window.location.search).get('roomsString') ||
    localStorage.getItem('roomsStringHotel') ||
    ''
  );
};

export const getOccupancyCodesFromRoomsString = (roomsString?: string) => {
  if (!roomsString) {
    return [];
  }

  const codes = roomsString
    .split(';')
    .filter(Boolean)
    .map((room) => {
      const [adultsPart, ...childParts] = room.split('_');
      const numAdt = parseInt(adultsPart, 10) || 0;
      const numCnn = childParts.filter((part) => part !== '').length;

      return moManualRoomTypeOptions.find(
        (option) => option.data.numAdt === numAdt && option.data.numCnn === numCnn,
      )?.value;
    })
    .filter((code): code is string => !!code);

  return Array.from(new Set(codes));
};

const isBedRuleVisible = (rule: BedRule) => rule.active !== 0 && rule.active !== '0';

export const getApplicableBedRules = (bedRules?: BedRuleGroup[], roomsString?: string) => {
  if (!bedRules?.length) {
    return [];
  }

  const occupancyCodes = getOccupancyCodesFromRoomsString(roomsString).map((code) =>
    code.toLowerCase(),
  );

  return bedRules
    .filter((group) => {
      const type = (group.type || '').toLowerCase();
      return type === 'general' || occupancyCodes.includes(type);
    })
    .map((group) => ({
      ...group,
      items: (group.items || []).filter(isBedRuleVisible),
    }))
    .filter((group) => group.items.length);
};

export const getHotelSupplierKey = (hotel: Hotel) =>
  hotel.item?.sysSupp || hotel.item?.sysSuppCode || 'unknown';

export const getSupplierItemKey = (supplier: SupplierItem) =>
  supplier.suppDesc || supplier.suppCode || 'unknown';

export const roomMatchesSupplier = (
  room: { suppDesc?: any; suppCode?: string },
  supplier: SupplierItem,
) =>
  (!!supplier.suppDesc && room.suppDesc === supplier.suppDesc) ||
  (!!supplier.suppCode && room.suppCode === supplier.suppCode);

export interface RoomTypeOption {
  name: string;
  checked: boolean;
  count: number;
  price: number;
  visible?: boolean;
}

export interface LocationValue {
  lat: number;
  lng: number;
  name: string;
  km?: number;
  isHotel?: boolean;
}

export const H_TYPE_OPTIONS = ['H', 'A', 'V'] as const;
export type HTypeCode = (typeof H_TYPE_OPTIONS)[number];

export const H_TYPE_LABELS: Record<HTypeCode, string> = {
  H: 'hotel.hotel',
  A: 'hotel.apartment',
  V: 'hotel.villa',
};

export interface HTypeFilter {
  counts: number[];
  options: string[];
  value: string[];
  labels: string[];
}

export const getHotelHType = (hotel: Hotel): HTypeCode | undefined => {
  const type = hotel.item?.type?.trim().toUpperCase();
  return H_TYPE_OPTIONS.includes(type as HTypeCode) ? (type as HTypeCode) : undefined;
};

export const getHTypeFilter = (hotels: Hotel[], selectedValues: string[] = []): HTypeFilter => {
  const countByType = new Map<HTypeCode, number>();

  hotels.forEach((hotel) => {
    const type = getHotelHType(hotel);
    if (!type) {
      return;
    }
    countByType.set(type, (countByType.get(type) || 0) + 1);
  });

  const options = H_TYPE_OPTIONS.filter((type) => countByType.has(type));
  const selected = selectedValues
    .map((value) => value.trim().toUpperCase())
    .filter((value): value is HTypeCode => options.includes(value as HTypeCode));

  return {
    options,
    value: Array.from(new Set(selected)),
    counts: options.map((type) => countByType.get(type) || 0),
    labels: options.map((type) => H_TYPE_LABELS[type]),
  };
};

export const mergeHTypeFilters = (
  current?: HTypeFilter,
  incoming?: HTypeFilter,
): HTypeFilter | undefined => {
  const countByType = new Map<HTypeCode, number>();

  const addCounts = (hType?: HTypeFilter) => {
    hType?.options.forEach((option, index) => {
      const type = option?.trim().toUpperCase();
      if (!H_TYPE_OPTIONS.includes(type as HTypeCode)) {
        return;
      }
      countByType.set(
        type as HTypeCode,
        (countByType.get(type as HTypeCode) || 0) + (hType.counts?.[index] || 0),
      );
    });
  };

  addCounts(current);
  addCounts(incoming);

  if (!countByType.size) {
    return current || incoming;
  }

  const options = H_TYPE_OPTIONS.filter((type) => countByType.has(type));
  const selected = [...(current?.value || []), ...(incoming?.value || [])]
    .map((value) => value.trim().toUpperCase())
    .filter((value): value is HTypeCode => options.includes(value as HTypeCode));

  return {
    options,
    value: Array.from(new Set(selected)),
    counts: options.map((type) => countByType.get(type) || 0),
    labels: options.map((type) => H_TYPE_LABELS[type]),
  };
};

export class FilterModel {
  name: string;
  room: string;
  price: {
    min: number;
    max: number;
    currencySign: string;
    value: [number, number];
  };
  stars: {
    options: number[];
    value: number[];
  };
  meal: {
    options: string[];
    active: string[];
    value: string[];
    tooltips?: string[];
  };
  location: {
    value: LocationValue;
    isHotel?: boolean;
    km?: number;
  };
  additional: {
    options: AvailableBoardType[];
    value: string[];
    freeCxl: boolean;
    withMeal: boolean;
    preffered: boolean;
    ada: boolean;
  };
  roomTypes: RoomTypeGrp[];
  suppliers: SupplierItem[];
  roomTypesArr?: string[];
  searchWithActiveFilters?: boolean;
  displayExactly?: boolean;
  hType?: HTypeFilter;
  bedRules?: BedRuleGroup[];
  bedFilter?: {
    include: string[];
    exclude: string[];
  };
  facilityFilter?: {
    include: string[];
  };
  pinOnly?: boolean;

  constructor(
    hotels: Hotel[],
    availableBoardTypes: AvailableBoardType[],
    currency: string,
    isFreeCxlDefault: boolean,
    hotelIndex?: number,
  ) {
    this.name = '';
    this.room = '';
    this.suppliers = [];
    this.facilityFilter = { include: [] };
    const initSiteData = JSON.parse(localStorage.getItem('initSiteData') || '{}');
    const bedRules = initSiteData.sharedBedRules;
    let bedRulesObj;
    try {
      bedRulesObj = bedRules ? JSON.parse(bedRules) : undefined;
    } catch {
      bedRulesObj = undefined;
    }
    this.bedRules = getApplicableBedRules(bedRulesObj, getSearchRoomsString());
    this.bedFilter = { include: [], exclude: [] };

    const filterHotels = sessionStorage.getItem(getKey('filterHotels', hotelIndex));
    const prevFilter = filterHotels ? JSON.parse(filterHotels) : undefined;
    this.searchWithActiveFilters = prevFilter?.searchWithActiveFilters || false;
    this.pinOnly = prevFilter?.pinOnly || false;

    this.hType = getHTypeFilter(hotels);

    const prices = hotels?.flatMap((hotel) =>
      hotel.roomClasses?.flatMap((room) => room.price[currency as keyof Price] || []),
    );

    let minPrice = Infinity;
    let maxPrice = -Infinity;

    prices.forEach((price) => {
      if (price < minPrice) minPrice = price;
      if (price > maxPrice) maxPrice = price;
    });

    minPrice = prices.length ? Math.floor(minPrice) : 0;
    maxPrice = prices.length ? Math.floor(maxPrice + 1) : 1;

    this.price = {
      min: minPrice,
      max: maxPrice,
      value: [minPrice, maxPrice],
      currencySign: getCurrency(currency),
    };
    const stars = Array.from(
      new Set(hotels.map((hotel) => Math.floor(hotel.item?.star || 0))),
    ).sort((a, b) => a - b);

    this.stars = {
      options: stars,
      value: [],
    };
    const mealOptions = ['BB', 'HB', 'FB', 'AI'];
    this.meal = {
      value: [],
      active: mealOptions.filter((mealCode) =>
        hotels.some((hot) => hot.roomClasses.some((rc) => rc.board.basis.code === mealCode)),
      ),
      options: mealOptions,
      tooltips: [
        'filter.breakfast',
        'filter.half_board',
        'filter.full_board',
        'filter.all_inclusive',
      ],
    };

    const suppliersMap = new Map<
      string,
      {
        suppDesc: string;
        suppCode: string;
        checked: boolean;
        hotels: number;
        rooms: number;
        price: number;
        hotelsArr: string[];
      }
    >();

    hotels.forEach((hotel) => {
      const suppDesc = getHotelSupplierKey(hotel);
      const suppCode = hotel.item?.sysSuppCode || '';
      const roomsCount = hotel.roomClasses?.length || 0;
      let minPrice = Infinity;

      hotel.roomClasses?.forEach((room) => {
        const price = room.price[currency as keyof Price];
        if (typeof price === 'number' && price < minPrice) {
          minPrice = price;
        }
      });

      const existing = suppliersMap.get(suppDesc);
      if (!existing) {
        suppliersMap.set(suppDesc, {
          suppDesc,
          suppCode,
          checked: false,
          hotels: 1,
          rooms: roomsCount,
          price: minPrice,
          hotelsArr: hotel.htuKey ? [hotel.htuKey] : [],
        });
      } else {
        existing.hotels += 1;
        existing.rooms += roomsCount;
        if (minPrice < existing.price) {
          existing.price = minPrice;
        }
        if (hotel.htuKey && !existing.hotelsArr.includes(hotel.htuKey)) {
          existing.hotelsArr.push(hotel.htuKey);
        }
      }
    });

    const suppliersList = Array.from(suppliersMap.values())?.sort((a, b) => {
      return a.suppDesc?.localeCompare(b.suppDesc);
    });

    this.suppliers = (suppliersList || []).map((supplier) => ({
      ...supplier,
      price: Number.isFinite(supplier.price) ? supplier.price : 0,
    }));

    this.location = {
      value: {
        lat: 0,
        lng: 0,
        name: '',
      },
    };

    this.additional = {
      options: availableBoardTypes,
      value: [],
      freeCxl: isFreeCxlDefault,
      withMeal: false,
      preffered: false,
      ada: false,
    };
    this.roomTypes = Object.keys(RoomType).map((key) => ({
      title: key,
      options: [],
      icon: roomTypesIcon[key as keyof typeof roomTypesIcon],
    }));

    this.roomTypes.forEach((type) => {
      const optionsMap = new Map<string, RoomTypeOption>();

      hotels.forEach((hotel) => {
        hotel.roomClasses.forEach((rClass) => {
          const valData = rClass.hotelRooms[0][type.title as keyof HotelRoom];
          if (!valData) return;

          valData.split(', ').forEach((val: any) => {
            if (!val) return;

            const existingOption = optionsMap.get(val);

            if (existingOption) {
              existingOption.count++;
              existingOption.price = Math.min(
                existingOption.price,
                rClass.price[currency as keyof Price],
              );
            } else {
              optionsMap.set(val, {
                name: val,
                checked: false,
                count: 1,
                price: rClass.price[currency as keyof Price],
                visible: true,
              });
            }
          });
        });
      });

      type.options = Array.from(optionsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    });
  }
}

export class FilterHotelModel {
  name: string;
  room: string;
  price: {
    min: number;
    max: number;
    currencySign: string;
    value: [number, number];
  };
  stars: {
    options: number[];
    value: number[];
  };
  meal: any;
  location: {
    value: LocationValue;
    isHotel?: boolean;
    km?: number;
  };
  additional: {
    options: BoardType[];
    value: string[];
    freeCxl: boolean;
    withMeal: boolean;
    preffered: boolean;
    ada: boolean;
  };
  roomTypes: RoomTypeGrp[];
  roomTypesArr?: string[];
  suppliers: SupplierItem[];

  constructor(
    hotels: Hotel[],
    selectedRoomPrice: number,
    numberOfPax: number,
    currency: string,
    isFreeCxlDefault: boolean,
  ) {
    this.name = '';
    this.room = '';
    this.roomTypes = [];
    this.suppliers = [];

    const prices = hotels.reduce((acc: number[], hotel) => {
      return [...acc, ...hotel.roomClasses.map((room) => room.price[currency as keyof Price])];
    }, []);

    const minPriceUniq = prices.length ? Math.min(...prices) : 0;
    const maxPriceUniq = prices.length ? Math.max(...prices) + 1 : 1;
    const minPrice = Math.floor(minPriceUniq / numberOfPax - selectedRoomPrice);
    const maxPrice = Math.ceil(maxPriceUniq / numberOfPax - selectedRoomPrice);
    this.price = {
      min: minPrice,
      max: maxPrice,
      value: [minPrice, maxPrice],
      currencySign: getCurrency(currency),
    };

    const stars: number[] = hotels
      .map((hotel) => Math.floor(hotel.item?.star || 0))
      .filter((star, index, arr) => arr.indexOf(star) === index)
      .sort((a, b) => b - a);
    this.stars = {
      options: stars,
      value: [],
    };
    this.meal = {
      active: [],
      value: [],
    };

    this.location = {
      value: {
        lat: 0,
        lng: 0,
        name: '',
      },
    };

    const availableBoardTypes = hotels
      .reduce((acc: BoardType[], hotel) => {
        var boardTypes: BoardType[] = hotel.roomClasses.map((room) => ({
          code: room.board.basis.code,
          name: room.board.basis.desc,
        }));
        return [...boardTypes, ...acc];
      }, [])
      .filter((board, index, arr) => arr.findIndex((b) => b.code === board.code) === index);

    this.additional = {
      options: availableBoardTypes,
      value: [],
      freeCxl: isFreeCxlDefault,
      withMeal: false,
      preffered: false,
      ada: false,
    };

    this.roomTypes = [];
    Object.keys(RoomType).forEach((key) => {
      this.roomTypes.push({ title: key, options: [] });
    });

    let suppliersList: SupplierItem[] = [];
    hotels.forEach((hotel) => {
      hotel.roomClasses.forEach((room) => {
        if (!suppliersList.some((sup) => sup.suppCode === room.suppCode)) {
          suppliersList.push({
            suppDesc: room.suppDesc || '',
            suppCode: room.suppCode || '',
            checked: false,
            hotels: 1,
            rooms: 1,
            price: room.price[currency as keyof Price],
            hotelsArr: [hotel.htuKey],
          });
        } else {
          const index = suppliersList.findIndex((sup) => sup.suppCode === room.suppCode);
          if (!suppliersList[index].hotelsArr?.includes(hotel.htuKey)) {
            suppliersList[index].hotelsArr?.push(hotel.htuKey);
          }
          suppliersList[index].hotels = suppliersList[index].hotelsArr?.length || 0;
          suppliersList[index].rooms += 1;
          if (suppliersList[index].price > room.price[currency as keyof Price]) {
            suppliersList[index].price = room.price[currency as keyof Price];
          }
        }
      });
    });
    suppliersList = suppliersList.sort((a, b) => a.suppCode?.localeCompare(b.suppCode));
    this.suppliers = suppliersList || [];

    this.roomTypesArr = [];
    this.roomTypes.forEach((type) => {
      let options: RoomTypeOption[] = [];

      hotels.forEach((hotel) => {
        hotel.roomClasses.forEach((rClass) => {
          rClass.hotelRooms.forEach((room) => {
            const valData = room[type.title as keyof HotelRoom];
            if (valData) {
              const valArr: string[] = room[type.title as keyof HotelRoom].split(', ');

              valArr.forEach((val) => {
                if (val) {
                  const alreadyExistsIndex = options.findIndex((option) => option.name === val);
                  if (alreadyExistsIndex === -1) {
                    options.push({
                      name: val,
                      checked: false,
                      count: 1,
                      price: rClass.price[currency as keyof Price],
                      visible: true,
                    });
                  } else {
                    options[alreadyExistsIndex].count++;
                    if (options[alreadyExistsIndex].price > rClass.price[currency as keyof Price]) {
                      options[alreadyExistsIndex].price = rClass.price[currency as keyof Price];
                    }
                  }
                }
              });
            }
          });
        });
      });

      options = options.sort((a, b) => a.name.localeCompare(b.name));
      type.options = options;
    });
  }
}
